import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  SolarContractLanguage,
  SolarContractReadinessDto,
} from "../dto/solar-contract.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarContractLanguageVariables = {
  proposalId: string
  language: SolarContractLanguage
}

/**
 * Chooses the contract package's language. The answer is the readiness read
 * itself (the template follows the language); a failure re-reads it, since a
 * lock that appeared is the usual reason.
 */
export default function useUpdateSolarContractLanguage(): UseMutationResult<
  SolarContractReadinessDto,
  unknown,
  UpdateSolarContractLanguageVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      proposalId,
      language,
    }: UpdateSolarContractLanguageVariables) =>
      solarProposalService.putContractLanguage(proposalId, { language }),
    onSuccess: (readiness, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.contract(proposalId),
        readiness,
      )
    },
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.contract(proposalId),
        exact: true,
      })
    },
  })
}
